import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFleetFilters } from '@/hooks/use-fleet-filters';
import { useFleetSummary } from '@/hooks/use-fleet-summary';
import type { FleetSummary } from '@/types/fleet';

import { MultiSelect } from './multi-select';

const ROLE_OPTIONS = [
  'vpn_server',
  'exit_node',
  'gateway',
  'management',
  'controller',
  'monitoring',
  'database',
  'sentry',
];

const STATUS_OPTIONS = ['active', 'provisioning', 'quarantined', 'decommissioned'];

const EMPTY_FILTERS: FleetSummary['filters'] = {
  providers: [],
  continents: [],
  countries: [],
  protocols: [],
  capabilities: [],
};

export function FilterBar(): JSX.Element {
  const { query, setMulti, setSearch, reset, isFiltered } = useFleetFilters();
  const { data: summary } = useFleetSummary();
  const filters = summary?.filters ?? EMPTY_FILTERS;

  // Debounce the search input — push to the URL 300ms after typing stops.
  const [searchInput, setSearchInput] = useState(query.q ?? '');

  useEffect(() => {
    setSearchInput(query.q ?? '');
  }, [query.q]);

  // Keep the latest setSearch in a ref so the debounce effect below does not
  // depend on its (unstable) identity — an unrelated re-render must not reset
  // the in-flight 300ms timer.
  const setSearchRef = useRef(setSearch);
  setSearchRef.current = setSearch;

  useEffect(() => {
    const current = query.q ?? '';

    if (searchInput === current) return undefined;

    const id = setTimeout(() => setSearchRef.current(searchInput), 300);

    return () => clearTimeout(id);
  }, [searchInput, query.q]);

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-border bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name…"
          className="h-9 w-56 pl-8 font-mono text-xs"
          aria-label="Search servers by name"
        />
      </div>

      <MultiSelect
        label="Role"
        options={ROLE_OPTIONS}
        selected={query.role ?? []}
        onChange={(v) => setMulti('role', v)}
        formatOption={(v) => v.replace('_', ' ')}
      />
      <MultiSelect
        label="Status"
        options={STATUS_OPTIONS}
        selected={query.status ?? []}
        onChange={(v) => setMulti('status', v)}
      />
      <MultiSelect
        label="Provider"
        options={filters.providers}
        selected={query.provider ?? []}
        onChange={(v) => setMulti('provider', v)}
      />
      <MultiSelect
        label="Continent"
        options={filters.continents}
        selected={query.region_continent ?? []}
        onChange={(v) => setMulti('region_continent', v)}
      />
      <MultiSelect
        label="Country"
        options={filters.countries}
        selected={query.region_country ?? []}
        onChange={(v) => setMulti('region_country', v)}
      />
      <MultiSelect
        label="Protocol"
        options={filters.protocols}
        selected={query.protocol ?? []}
        onChange={(v) => setMulti('protocol', v)}
      />
      <MultiSelect
        label="Capability"
        options={filters.capabilities}
        selected={query.capability ?? []}
        onChange={(v) => setMulti('capability', v)}
      />

      {isFiltered && (
        <Button variant="ghost" size="sm" onClick={reset} className="h-9">
          <X className="h-4 w-4" aria-hidden />
          Reset
        </Button>
      )}
    </div>
  );
}
